import { unwrap, type components } from "@wilco/api";
import { api } from "../auth.ts";
import { idempotency } from "../docs/DocumentForm.tsx";
import { partnerName, productInfos } from "../lookups.ts";

/*
 * Продажи на REST из contracts/openapi.json (модуль sales). Заказ отдаёт id
 * покупателя и изделия без названий: названия из справочника и partners.
 * Счетов покупателям нет (Р2): выручка и себестоимость признаются отгрузкой.
 */
type S = components["schemas"];

export type SalesState = "DRAFT" | "CONFIRMED" | "DONE" | "CANCELLED";

export type SalesLine = S["SalesLineView"] & {
  productName: string;
  productCode: string;
};

export interface SalesOrder extends Omit<S["SalesOrderView"], "lines" | "state"> {
  state: SalesState;
  customerName: string;
  lines: SalesLine[];
}

export type Candidate = S["SalesCandidate"];
export type ShipResult = S["SalesShipResult"];
export type ShipTask = S["SalesShipTask"];
export type Shortage = S["SalesShortage"];

export interface Customer {
  id: string;
  name: string;
}

const id = (v: string) => ({ params: { path: { id: v } } });
const serial = (v: string) => ({ params: { path: { serial: v } } });

async function enrich(o: S["SalesOrderView"]): Promise<SalesOrder> {
  const [customerName, info] = await Promise.all([o.customer ? Promise.resolve(o.customer) : partnerName(o.customerId), productInfos(o.lines.map((l) => l.productId))]);
  return {
    ...o,
    state: o.state as SalesState,
    customerName,
    lines: o.lines.map((l) => {
      const p = info.get(l.productId);
      return { ...l, productName: p?.name ?? l.productId, productCode: p?.sku ?? l.productId };
    }),
  };
}

const orderOf = async (p: Promise<{ data?: S["SalesOrderView"]; error?: unknown; response: Response }>) => enrich(await unwrap(p));

export const salesApi = {
  orders: async (): Promise<SalesOrder[]> => Promise.all((await unwrap(api.GET("/api/v1/sales/orders"))).map(enrich)),
  order: (v: string) => orderOf(api.GET("/api/v1/sales/orders/{id}", id(v))),
  create: (customerId: string, shipDate: string, key: string) =>
    orderOf(api.POST("/api/v1/sales/orders", { body: { customerId, shipDate }, headers: idempotency(key) })),
  addLine: (v: string, body: S["SalesLineInput"]) => orderOf(api.POST("/api/v1/sales/orders/{id}/lines", { ...id(v), body })),
  confirm: (v: string) => orderOf(api.POST("/api/v1/sales/orders/{id}/confirm", id(v))),
  cancel: (v: string) => orderOf(api.POST("/api/v1/sales/orders/{id}/cancel", id(v))),
  shortage: (v: string) => unwrap(api.GET("/api/v1/sales/orders/{id}/shortage", id(v))),
  candidates: (v: string, no: number) => unwrap(api.GET("/api/v1/sales/orders/{id}/lines/{no}/candidates", { params: { path: { id: v, no } } })),
  ship: (v: string, no: number, s: string) =>
    unwrap(api.POST("/api/v1/sales/orders/{id}/lines/{no}/shipments", { params: { path: { id: v, no } }, body: { serial: s } })),
  tasks: () => unwrap(api.GET("/api/v1/sales/tasks/ship")),
  returnUnit: (s: string) => unwrap(api.POST("/api/v1/sales/units/{serial}/return", serial(s))).then(() => null),
  refusal: (s: string, reason: string) => unwrap(api.POST("/api/v1/sales/units/{serial}/refusal", { ...serial(s), body: { reason } })).then(() => null),
  reship: (s: string) => unwrap(api.POST("/api/v1/sales/units/{serial}/reship", serial(s))),
  /** Покупатели: контрагенты с отметкой «покупець», не в архиве. */
  customers: async (): Promise<Customer[]> => {
    const all = await unwrap(api.GET("/api/v1/partners", { params: { query: { limit: 200 } } }));
    return all.filter((p) => p.customer && !p.archived).map((p) => ({ id: p.id, name: p.name }));
  },
};
